// src/pages/LocationPayments.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// ====== USTAWIENIA ======
const FACILITY_ID = "SPdqELFz2ovWSn8eFyws";

/**
 * Twardszy fetch: retry + backoff, żeby "nie gubiło" płatności przez chwilowe błędy sieci/funkcji.
 */
async function fetchMonthlyTotal(stripeId, year, month, { retries = 3 } = {}) {
  if (!stripeId) return 0;

  const url = `https://europe-central2-modit-system.cloudfunctions.net/getUserMonthlyTotal`;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stripeId, year, month }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const total = Number(data?.total || 0);
      return Number.isFinite(total) ? total : 0;
    } catch (e) {
      const last = attempt === retries;
      console.error("fetchMonthlyTotal error:", e, { stripeId, year, month, attempt });
      if (last) return 0;
      await sleep(250 * Math.pow(2, attempt)); // 250ms, 500ms, 1000ms...
    }
  }
  return 0;
}

/**
 * Limit równoległości requestów, żeby nie zabić funkcji / przeglądarki / nie wpaść w throttling.
 */
async function mapLimit(items, limit, mapper) {
  const ret = [];
  let idx = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (idx < items.length) {
      const i = idx++;
      ret[i] = await mapper(items[i], i);
    }
  });

  await Promise.all(workers);
  return ret;
}

const MONTHS_SEASON = [
  { label: "Wrzesień", value: 9 },
  { label: "Październik", value: 10 },
  { label: "Listopad", value: 11 },
  { label: "Grudzień", value: 12 },
  { label: "Styczeń", value: 1 },
  { label: "Luty", value: 2 },
  { label: "Marzec", value: 3 },
  { label: "Kwiecień", value: 4 },
  { label: "Maj", value: 5 },
  { label: "Czerwiec", value: 6 },
  { label: "Lipiec", value: 7 },
  { label: "Sierpień", value: 8 },
];

function weekDayNamePL(d) {
  if (d === 1) return "Poniedziałek";
  if (d === 2) return "Wtorek";
  if (d === 3) return "Środa";
  if (d === 4) return "Czwartek";
  if (d === 5) return "Piątek";
  if (d === 6) return "Sobota";
  if (d === 7 || d === 0) return "Niedziela";
  return "";
}

function unique(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

/**
 * ✅ KLUCZOWE: payerId = UID płatnika / rodziny.
 * Jeśli rodzeństwo ma ten sam uid -> płatność liczona 1x.
 *
 * DOSTOSUJ PRIORYTET jeśli u Ciebie uid jest w innym polu.
 */
function getPayerId(acc, su) {
  return (
    su?.uid || // <-- najważniejsze (rodzeństwo)
    acc?.uid ||
    su?.stripeId ||
    acc?.stripeId ||
    ""
  );
}

export default function MonthlyPayments() {
  // --- lokalizacje z doc("locations", FACILITY_ID) (każda lokalizacja to osobny obiekt w mapie)
  const [locationsMap, setLocationsMap] = useState({});
  // --- grupy z doc("groups", FACILITY_ID) (mapa -> tablica)
  const [groupsMap, setGroupsMap] = useState({});
  // --- użytkownicy z users2026 (konto + subUsers)
  const [accounts, setAccounts] = useState([]);

  // wybór lokalizacji, grupy oraz miesiąca
  const [selectedLocId, setSelectedLocId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(9); // domyślnie wrzesień
  const [seasonStartYear, setSeasonStartYear] = useState(2025); // rok startowy sezonu (wrz–sie)

  // cache kwot stripe: key = `${stripeId}:${year}-${month}`
  const [monthlyCache, setMonthlyCache] = useState({});

  // ============ Subskrypcje ============

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "locations", FACILITY_ID), (snap) => {
      setLocationsMap(snap.data() || {});
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "groups", FACILITY_ID), (snap) => {
      setGroupsMap(snap.data() || {});
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users2026"), (snap) => {
      const arr = snap.docs.map((d) => d.data());
      setAccounts(arr || []);
    });
    return () => unsub();
  }, []);

  // ============ Przetwarzanie danych ============

  const groupsArray = useMemo(() => {
    // mapa -> posortowana po godzinie i dniu
    const arr = Object.keys(groupsMap).map((id) => ({ id, ...groupsMap[id] }));
    arr.sort((a, b) => {
      const locA = a?.location_id || "";
      const locB = b?.location_id || "";
      if (locA !== locB) return locA.localeCompare(locB, "pl");

      const dayA = Array.isArray(a.days) ? a.days[0]?.weekDay : a?.days?.weekDay;
      const dayB = Array.isArray(b.days) ? b.days[0]?.weekDay : b?.days?.weekDay;
      if ((dayA || 0) !== (dayB || 0)) return (dayA || 0) - (dayB || 0);

      const timeA = (Array.isArray(a.days) ? a.days[0]?.startTime : a?.days?.startTime) || "";
      const timeB = (Array.isArray(b.days) ? b.days[0]?.startTime : b?.days?.startTime) || "";
      return timeA.localeCompare(timeB);
    });
    return arr;
  }, [groupsMap]);

  const groupsByLocation = useMemo(() => {
    const byLoc = {};
    groupsArray.forEach((g) => {
      const locId = g?.location_id || "—";
      if (!byLoc[locId]) byLoc[locId] = [];
      byLoc[locId].push(g);
    });
    return byLoc;
  }, [groupsArray]);

  // indeks grup po id do szybkiego opisu „druga grupa”
  const groupIndex = useMemo(() => {
    const m = new Map();
    groupsArray.forEach((g) => m.set(g.id, g));
    return m;
  }, [groupsArray]);

  /**
   * ✅ META PŁATNIKA:
   * payerId -> { stripeId, groupIds:Set, names:Set }
   * - jeden płatnik może mieć wiele dzieci (names)
   * - i wiele grup (przez wszystkie lokalizacje)
   */
  const payerMeta = useMemo(() => {
    const map = new Map();

    accounts.forEach((acc) => {
      const subUsers = Object.values(acc?.subUsers || {}) || [];
      subUsers.forEach((su) => {
        const payerId = getPayerId(acc, su);
        if (!payerId) return;

        const stripeId = su?.stripeId || acc?.stripeId || "";
        const groups = Array.isArray(su?.groups) ? su.groups : [];

        if (!map.has(payerId)) {
          map.set(payerId, {
            payerId,
            stripeId,
            groupIds: new Set(),
            names: new Set(),
          });
        }

        const pm = map.get(payerId);

        // zachowaj pierwsze sensowne stripeId
        if (!pm.stripeId && stripeId) pm.stripeId = stripeId;

        if (su?.displayName) pm.names.add(su.displayName);

        groups.forEach((gid) => pm.groupIds.add(gid));
      });
    });

    return map;
  }, [accounts]);

  /**
   * ✅ Członkowie grup z dedup per payerId.
   * W jednej grupie płatnik pojawia się 1x nawet jeśli ma tam 2 dzieci.
   */
  const membersByGroup = useMemo(() => {
    const map = {}; // { [groupId]: [ { payerId, names[], stripeId, payerGroupsCount, otherGroups[] } ] }

    payerMeta.forEach((pm) => {
      const allGroupIds = Array.from(pm.groupIds);
      const payerGroupsCount = allGroupIds.length || 1;

      allGroupIds.forEach((gid) => {
        if (!map[gid]) map[gid] = [];
        map[gid].push({
          payerId: pm.payerId,
          names: Array.from(pm.names),
          stripeId: pm.stripeId || "",
          payerGroupsCount,
          otherGroups: allGroupIds.filter((x) => x !== gid),
        });
      });
    });

    // safety: 100% unikalny payerId w grupie (merge names)
    Object.keys(map).forEach((gid) => {
      const byPayer = new Map();
      map[gid].forEach((m) => {
        if (!byPayer.has(m.payerId)) byPayer.set(m.payerId, m);
        else {
          const prev = byPayer.get(m.payerId);
          prev.names = unique([...(prev.names || []), ...(m.names || [])]);
          if (!prev.stripeId && m.stripeId) prev.stripeId = m.stripeId;
          prev.payerGroupsCount = Math.max(prev.payerGroupsCount || 1, m.payerGroupsCount || 1);
          prev.otherGroups = unique([...(prev.otherGroups || []), ...(m.otherGroups || [])]);
        }
      });

      map[gid] = Array.from(byPayer.values()).sort((a, b) =>
        (a.names?.[0] || "").localeCompare(b.names?.[0] || "", "pl")
      );
    });

    return map;
  }, [payerMeta]);

  // ============ Obliczenia kwot (podział na liczbę grup PŁATNIKA) ============

  function deriveYearMonthFromSeason(seasonStart, month) {
    // Sezon: wrzesień(seasonStart) .. grudzień(seasonStart), styczeń..sierpień(seasonStart+1)
    const m = Number(month);
    const year = m >= 9 ? seasonStart : seasonStart + 1;
    return { year, month: m };
  }

  function formatGroupLabel(g) {
    const day = Array.isArray(g.days) ? g.days[0] : g.days;
    const dayName = weekDayNamePL(day?.weekDay);
    const time = `${day?.startTime} - ${day?.endTime}`;
    const type = day?.custom_name ? day.custom_name : g?.type || "";
    const locName = (locationsMap[g?.location_id] && locationsMap[g?.location_id].name) || "—";
    return `${locName} - ${dayName} - ${time} - ${type}`;
  }

  function getStripeMonthlyFromCache(stripeId) {
    const { year, month } = deriveYearMonthFromSeason(seasonStartYear, selectedMonth);
    const key = `${stripeId}:${year}-${month}`;
    const v = monthlyCache[key];
    return typeof v === "number" ? v : 0;
  }

  /**
   * ✅ Fetch kwot per STRIPE ID w danej lokalizacji.
   * - retry
   * - limit concurrency (8)
   * - cache
   */
  useEffect(() => {
    if (!selectedLocId) return;

    const locGroups = groupsByLocation[selectedLocId] || [];
    const stripeIds = new Set();

    locGroups.forEach((g) => {
      const list = membersByGroup[g.id] || [];
      list.forEach((m) => {
        if (m.stripeId) stripeIds.add(m.stripeId);
      });
    });

    const { year, month } = deriveYearMonthFromSeason(seasonStartYear, selectedMonth);

    const toFetch = [];
    stripeIds.forEach((sid) => {
      const key = `${sid}:${year}-${month}`;
      if (monthlyCache[key] === undefined) toFetch.push(sid);
    });

    if (toFetch.length === 0) return;

    let cancelled = false;

    (async () => {
      const entries = await mapLimit(toFetch, 8, async (sid) => {
        const total = await fetchMonthlyTotal(sid, year, month, { retries: 3 });
        return [`${sid}:${year}-${month}`, total];
      });

      if (cancelled) return;

      setMonthlyCache((prev) => {
        const next = { ...prev };
        entries.forEach(([k, v]) => (next[k] = v));
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocId, groupsByLocation, membersByGroup, seasonStartYear, selectedMonth]);

  // sumy per grupa i lokalizacja
  const computedByGroup = useMemo(() => {
    if (!selectedLocId) return {};
    const result = {};
    const locGroups = groupsByLocation[selectedLocId] || [];

    locGroups.forEach((g) => {
      const members = (membersByGroup[g.id] || []).slice().sort((a, b) =>
        (a?.names?.[0] || "").localeCompare(b?.names?.[0] || "", "pl")
      );

      const rows = members.map((m) => {
        const baseTotal = getStripeMonthlyFromCache(m.stripeId);

        // ✅ podział płatności na liczbę grup PŁATNIKA (po wszystkich lokalizacjach)
        const share =
          m.payerGroupsCount && m.payerGroupsCount > 0
            ? baseTotal / m.payerGroupsCount
            : baseTotal;

        const other = (m.otherGroups || [])
          .map((gid) => {
            const gg = groupIndex.get(gid);
            if (!gg) return null;
            const d = Array.isArray(gg.days) ? gg.days[0] : gg.days;
            const label = `${weekDayNamePL(d?.weekDay)} ${d?.startTime}-${d?.endTime} ${
              d?.custom_name ? d.custom_name : gg?.type || ""
            }`;
            const locName =
              (locationsMap[gg?.location_id] && locationsMap[gg?.location_id].name) || "—";
            return `${locName} / ${label}`;
          })
          .filter(Boolean);

        const displayName =
          m.names && m.names.length > 1
            ? `${m.names.join(" + ")} (rodzeństwo)`
            : m.names?.[0] || "(bez nazwy)";

        return {
          name: displayName,
          stripeId: m?.stripeId || "",
          groupsCount: m?.payerGroupsCount || 1,
          otherGroupsLabels: other,
          share: Math.round(share * 100) / 100,
        };
      });

      const groupSum = Math.round(rows.reduce((s, r) => s + (r.share || 0), 0) * 100) / 100;

      result[g.id] = {
        group: g,
        label: formatGroupLabel(g),
        rows,
        sum: groupSum,
      };
    });

    return result;
  }, [
    selectedLocId,
    groupsByLocation,
    membersByGroup,
    monthlyCache,
    seasonStartYear,
    selectedMonth,
    groupIndex,
    locationsMap,
  ]);

  const locationGrandTotal = useMemo(() => {
    const groups = Object.values(computedByGroup || {});
    const sum = Math.round(groups.reduce((s, g) => s + (g?.sum || 0), 0) * 100) / 100;
    return sum;
  }, [computedByGroup]);

  // ============ UI ============

  const locArray = useMemo(() => {
    const arr = Object.values(locationsMap || {}).filter(Boolean);
    arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "pl"));
    return arr;
  }, [locationsMap]);

  const locNameSelected = (locationsMap[selectedLocId] && locationsMap[selectedLocId].name) || "";

  // rok docelowy do wyświetlenia obok miesiąca
  const { year: effectiveYear } = deriveYearMonthFromSeason(seasonStartYear, selectedMonth);

  return (
    <div
      style={{
        display: "flex",
        gap: 24,
        flexDirection: "column",
        padding: "20px",
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <h2 style={{ margin: 0 }}>Rozliczenia wg lokalizacji i grup</h2>

      {/* Wybór lokalizacji */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontWeight: 600 }}>Lokalizacja:</label>
        <select
          value={selectedLocId}
          onChange={(e) => setSelectedLocId(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #bbb" }}
        >
          <option value="">— wybierz —</option>
          {locArray.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>

        {/* Sezon start (rok) */}
        <label style={{ fontWeight: 600, marginLeft: 18 }}>Sezon od roku:</label>
        <input
          type="number"
          value={seasonStartYear}
          onChange={(e) => setSeasonStartYear(parseInt(e.target.value || "2025", 10))}
          style={{ width: 90, padding: "6px 10px", borderRadius: 6, border: "1px solid #bbb" }}
        />

        {/* Miesiąc (wrz–sie) */}
        <label style={{ fontWeight: 600, marginLeft: 18 }}>Miesiąc:</label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #bbb" }}
        >
          {MONTHS_SEASON.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        <span style={{ color: "#666" }}>
          (rozliczenie: {MONTHS_SEASON.find((m) => m.value === selectedMonth)?.label}{" "}
          {effectiveYear})
        </span>
      </div>

      {/* Grupy w wybranej lokalizacji */}
      {selectedLocId ? (
        <>
          <h3 style={{ margin: "8px 0 0" }}>
            {locNameSelected ? `Lokalizacja: ${locNameSelected}` : ""}
          </h3>

          {Object.values(computedByGroup).length === 0 && (
            <div style={{ color: "#777" }}>Brak grup w tej lokalizacji.</div>
          )}

          {Object.values(computedByGroup)
            .sort((a, b) => a.label.localeCompare(b.label, "pl"))
            .map(({ group, label, rows, sum }) => (
              <div
                key={group.id}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  padding: 14,
                  background: "#fff",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{label}</div>

                {/* Lista członków z kwotą (po podziale) */}
                {rows.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {rows.map((r, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "4px 0",
                          borderBottom: "1px dashed #f0f0f0",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{r.name}</div>

                          {r.groupsCount > 1 && (
                            <div style={{ fontSize: 12, color: "#a13" }}>
                              płatność dzielona na {r.groupsCount} grup &nbsp;–&nbsp;
                              {r.otherGroupsLabels.length > 0 ? (
                                <span>pozostałe: {r.otherGroupsLabels.join(" • ")}</span>
                              ) : (
                                <span>pozostałe grupy poza widokiem</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ minWidth: 120, textAlign: "right", fontWeight: 700 }}>
                          {r.share.toFixed(2)} zł
                          {r.groupsCount > 1 && (
                            <span style={{ color: "#666", fontWeight: 400 }}>
                              &nbsp;({`1/${r.groupsCount}`})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: 700,
                      }}
                    >
                      <span>Suma grupy</span>
                      <span>{sum.toFixed(2)} zł</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "#777" }}>Brak członków w tej grupie.</div>
                )}
              </div>
            ))}

          {/* SUMA LOKALIZACJI */}
          <div
            style={{
              marginTop: 10,
              borderTop: "2px solid #ddd",
              paddingTop: 10,
              fontWeight: 800,
              fontSize: 16,
              textAlign: "right",
            }}
          >
            Suma całej lokalizacji: {locationGrandTotal.toFixed(2)} zł
          </div>
        </>
      ) : (
        <div style={{ color: "#777" }}>
          Wybierz lokalizację, aby wyświetlić grupy i rozliczenia.
        </div>
      )}
    </div>
  );
}
